import * as React from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Boton, EsqueletoTabla, Input, Pill, PuntoNaranja, Tabla, TablaCabecera, TablaCelda, TablaCuerpo, TablaEncabezado, TablaFila, TituloPagina, Vacio } from "@iavmx/ui";
import { leer } from "../api";
import { relativo } from "../formato";
import { useSesion } from "../sesion";
import type { CuentaLista } from "../tipos";

const ESTADO: Record<string, { texto: string; tono: "neutro" | "verde" | "ambar" | "rojo" }> = {
  prospecto: { texto: "Prospecto", tono: "neutro" },
  activo: { texto: "Activo", tono: "verde" },
  inactivo: { texto: "Inactivo", tono: "ambar" },
  perdido: { texto: "Perdido", tono: "rojo" },
};

export function Cuentas() {
  const { api, listo } = useSesion();
  const navegar = useNavigate();
  const busqueda = useSearch({ from: "/cuentas" });
  const [q, setQ] = React.useState(busqueda.q ?? "");
  const [enviada, setEnviada] = React.useState(busqueda.q ?? "");
  React.useEffect(() => {
    const t = setTimeout(() => setEnviada(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const lista = useQuery({
    queryKey: ["cuentas", enviada],
    queryFn: () => api.api.cuentas.$get({ query: enviada ? { q: enviada } : {} }).then((r) => leer<{ cuentas: CuentaLista[]; total: number }>(r)),
    enabled: listo,
  });
  return (
    <>
      <TituloPagina
        descripcion="Registro único: todo el equipo ve la prospección para no duplicar propuestas."
        accion={
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <span className="sr-only">Buscar por razón social, nombre comercial o RFC</span>
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-tinta-tenue" aria-hidden />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Razón social, nombre comercial o RFC" className="h-8 w-64 pl-8 text-base sm:text-sm" />
            </label>
            <Boton variante="acento" tamano="sm" asChild>
              <Link to="/cuentas/nueva">Nueva cuenta</Link>
            </Boton>
          </div>
        }
      >
        Cuentas
        <PuntoNaranja />
      </TituloPagina>
      {lista.isLoading && <EsqueletoTabla />}
      {lista.error && <Vacio titulo="No se pudieron cargar las cuentas" texto={lista.error.message} />}
      {lista.data && (
        <>
          <p className="mb-2 font-ui text-xs text-tinta-tenue" aria-live="polite">
            {lista.data.total} cuenta{lista.data.total === 1 ? "" : "s"}
            {enviada ? ` que coinciden con «${enviada}»` : ""}
          </p>
          {lista.data.cuentas.length === 0 ? (
            <Vacio titulo={enviada ? "Sin coincidencias" : "Sin cuentas todavía"} texto={enviada ? "Prueba con menos palabras o con el RFC." : "La primera cuenta se da de alta con el botón de arriba."} />
          ) : (
            <Tabla>
              <TablaCabecera>
                <TablaFila>
                  <TablaEncabezado>Cuenta</TablaEncabezado>
                  <TablaEncabezado>RFC</TablaEncabezado>
                  <TablaEncabezado>Ejecutivo</TablaEncabezado>
                  <TablaEncabezado>Estado</TablaEncabezado>
                  <TablaEncabezado className="text-right">Abiertas</TablaEncabezado>
                  <TablaEncabezado>Última actividad</TablaEncabezado>
                </TablaFila>
              </TablaCabecera>
              <TablaCuerpo>
                {lista.data.cuentas.map((c) => {
                  const e = ESTADO[c.estado] ?? ESTADO.prospecto!;
                  return (
                    <TablaFila key={c.id} className="cursor-pointer" onClick={() => void navegar({ to: "/cuentas/$id", params: { id: c.id } })}>
                      <TablaCelda>
                        <Link to="/cuentas/$id" params={{ id: c.id }} className="font-semibold text-tinta hover:underline">
                          {c.razonSocial}
                        </Link>
                        {c.nombreComercial && <div className="font-ui text-xs text-tinta-tenue">{c.nombreComercial}</div>}
                        {c.odooId && <div className="font-ui text-[11px] text-tinta-tenue">Odoo #{c.odooId}</div>}
                      </TablaCelda>
                      <TablaCelda className="font-mono text-xs">{c.rfc ?? "—"}</TablaCelda>
                      <TablaCelda>{c.ejecutivo ?? "Sin asignar"}</TablaCelda>
                      <TablaCelda>
                        <Pill tono={e.tono}>{e.texto}</Pill>
                      </TablaCelda>
                      <TablaCelda className="text-right tabular-nums">{c.abiertas}</TablaCelda>
                      <TablaCelda className="text-tinta-tenue">{c.ultimaActividadEn ? relativo(c.ultimaActividadEn) : "—"}</TablaCelda>
                    </TablaFila>
                  );
                })}
              </TablaCuerpo>
            </Tabla>
          )}
        </>
      )}
    </>
  );
}
